/**
 * The object's RULES as plain list items: everything that makes a drawing
 * adapt beyond the basic editor tools — computed values (formulas), linked
 * values (step ↔ count, a depth that follows params), handles that set
 * params, shapes whose points follow formulas, and groups placed by rules
 * (copies, turns/shifts, show-if). Pinned points and the AI make them;
 * the editor lists them by what they ARE and lets users delete them in bulk.
 *
 * Deleting a rule FREEZES what depended on it at its look at the default
 * size: a removed value is replaced by its number wherever it was used, a
 * shape's formula points become fixed points, copies become plain shapes.
 * The drawing looks the same; placed objects just stop adapting that way.
 */

import type { ObjectDefinitionSpec, ObjectGroup, ObjectShape } from 'shared-types';
import { isFormulaHandlePoint } from 'shared-types';
import { defaultEvaluationVars, expressionValue, resolveShapeExpressions } from '../../../potree/sketchObjects/custom/evaluateCustomGlyph';
import { mapExpressions, rotateShapes, roundCoord, translateShapes } from './specOps';

type Spec = ObjectDefinitionSpec;

export type RuleKind = 'value' | 'link' | 'handle' | 'shape' | 'group';

export interface ObjectRule {
  /** Stable within a spec: `value:<key>`, `sync:<i>`, `depth`, `handle:<i>`, `ends`, `shape:<id>`, `group:<id>`. */
  id: string;
  kind: RuleKind;
  /** Its name when it has one (label / shape or group name); null → the list composes one from `subject`. */
  label: string | null;
  /** What the unnamed ones are about (a formula key, the linked param labels, a shape kind). */
  subject: string;
  /** A shape or group the rule belongs to (selected when the row is clicked). */
  target?: { kind: 'shape' | 'group'; id: string };
}

/** `leftLegLength` → "Left leg length". */
export function humanizeKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const paramLabel = (spec: Spec, key: string): string => spec.params.find((p) => p.key === key)?.label ?? humanizeKey(key);

/** Every rule of the spec, in a stable order (values, links, handles, groups, shapes). */
export function listRules(spec: Spec): ObjectRule[] {
  const rules: ObjectRule[] = [];
  for (const f of spec.formulas ?? []) rules.push({ id: `value:${f.key}`, kind: 'value', label: f.label ?? null, subject: humanizeKey(f.key) });
  (spec.stepSyncs ?? []).forEach((sync, i) => rules.push({ id: `sync:${i}`, kind: 'link', label: sync.label ?? null, subject: `${paramLabel(spec, sync.step)} · ${paramLabel(spec, sync.count)}` }));
  if (spec.depthLink) rules.push({ id: 'depth', kind: 'link', label: spec.depthLink.label ?? null, subject: 'depth' });
  if ((spec.handles?.start && isFormulaHandlePoint(spec.handles.start)) || (spec.handles?.end && isFormulaHandlePoint(spec.handles.end))) rules.push({ id: 'ends', kind: 'handle', label: null, subject: 'ends' });
  (spec.handles?.depth ?? []).forEach((d, i) => {
    if (!d.assign && !d.at) return;
    const params = (d.assign ?? []).map((a) => paramLabel(spec, a.param));
    rules.push({ id: `handle:${i}`, kind: 'handle', label: d.label ?? null, subject: params.length > 0 ? params.join(' · ') : 'depth' });
  });
  for (const g of spec.groups) {
    if (g.repeat?.kind === 'index' || g.offset || g.visibleIf) rules.push({ id: `group:${g.id}`, kind: 'group', label: g.name || null, subject: g.id, target: { kind: 'group', id: g.id } });
  }
  for (const s of spec.shapes) {
    if (s.expr) rules.push({ id: `shape:${s.id}`, kind: 'shape', label: s.name ?? null, subject: s.kind, target: { kind: 'shape', id: s.id } });
  }
  return rules;
}

const withoutExpr = (shape: ObjectShape): ObjectShape => {
  const { expr: _expr, ...rest } = shape;
  return rest;
};

/** A shape frozen at `vars`: formula fields become their values (rounded like authored coordinates). */
function bakeShape(shape: ObjectShape, vars: Readonly<Record<string, number>>): ObjectShape {
  if (!shape.expr) return shape;
  const resolved = resolveShapeExpressions(shape, vars);
  const r = (v: number): number => roundCoord(v);
  switch (resolved.kind) {
    case 'polyline':
      return withoutExpr({ ...resolved, points: resolved.points.map(([u, n]) => [r(u), r(n)]) });
    case 'rect':
      return withoutExpr({ ...resolved, min: [r(resolved.min[0]), r(resolved.min[1])], max: [r(resolved.max[0]), r(resolved.max[1])] });
    case 'circle':
      return withoutExpr({ ...resolved, center: [r(resolved.center[0]), r(resolved.center[1])], radius: r(resolved.radius) });
    case 'arc':
      return withoutExpr({ ...resolved, center: [r(resolved.center[0]), r(resolved.center[1])], radius: r(resolved.radius) });
    case 'path':
      return withoutExpr(resolved);
  }
}

/** A group without its rules: copies unrolled into plain shapes, turn/shift baked in, the condition resolved. */
function freezeGroup(spec: Spec, group: ObjectGroup, vars: Readonly<Record<string, number>>): Spec {
  const members = spec.shapes.filter((s) => group.shapeIds.includes(s.id));
  const countBinding = group.repeat?.kind === 'index' ? group.repeat.count : null;
  const count = countBinding ? Math.max(0, Math.round('value' in countBinding ? countBinding.value : 'expr' in countBinding ? expressionValue(countBinding.expr, vars) : (vars[countBinding.param] ?? 0))) : 1;
  const frozen: ObjectShape[] = [];
  for (let i = 0; i < count; i++) {
    const copyVars = { ...vars, i, count };
    const visible = group.visibleIf === undefined || expressionValue(group.visibleIf, copyVars) !== 0;
    const turn = expressionValue(group.offset?.rotate, copyVars);
    const du = expressionValue(group.offset?.u, copyVars);
    const dn = expressionValue(group.offset?.n, copyVars);
    let copies: Spec = { ...spec, shapes: members.map((s) => ({ ...bakeShape(s, copyVars), id: i === 0 ? s.id : `${s.id}_${i}` })) };
    const ids = copies.shapes.map((s) => s.id);
    if (turn) copies = rotateShapes(copies, ids, [0, 0], turn);
    if (du || dn) copies = translateShapes(copies, ids, du, dn);
    frozen.push(...copies.shapes.map((s) => (visible ? s : { ...s, hidden: true })));
  }
  const frozenIds = frozen.map((s) => s.id);
  const firstIndex = spec.shapes.findIndex((s) => group.shapeIds.includes(s.id));
  const others = spec.shapes.filter((s) => !group.shapeIds.includes(s.id));
  const shapes = [...others.slice(0, Math.max(0, firstIndex)), ...frozen, ...others.slice(Math.max(0, firstIndex))];
  const { repeat: _repeat, offset: _offset, visibleIf: _visibleIf, ...rest } = group;
  const plain: ObjectGroup = { ...rest, ...(group.repeat && group.repeat.kind !== 'index' ? { repeat: group.repeat } : {}), shapeIds: frozenIds, resize: group.resize.u === 'fixed' && group.resize.n === 'fixed' ? { u: 'stretch', n: 'stretch' } : group.resize };
  // A group left without shapes (0 copies) goes.
  return { ...spec, shapes, groups: frozenIds.length > 0 ? spec.groups.map((g) => (g.id === group.id ? plain : g)) : spec.groups.filter((g) => g.id !== group.id) };
}

/**
 * Remove the rules `ids` (see the module doc: dependents are frozen at the
 * default size, so the drawing looks the same and the spec stays valid).
 */
export function removeRules(spec: Spec, ids: readonly string[]): Spec {
  const remove = new Set(ids);
  // Every value as it is now, at the default size and params — what frozen things keep.
  const vars = defaultEvaluationVars(spec);
  let s = spec;

  for (const group of spec.groups) {
    if (remove.has(`group:${group.id}`)) {
      const current = s.groups.find((g) => g.id === group.id);
      if (current) s = freezeGroup(s, current, vars);
    }
  }

  const bakedIds = new Set(spec.shapes.filter((x) => remove.has(`shape:${x.id}`)).map((x) => x.id));
  if (bakedIds.size > 0) {
    const shapes = s.shapes.map((x) => (bakedIds.has(x.id) ? bakeShape(x, { ...vars, i: 0, count: 1 }) : x));
    // A formula group whose shapes are all plain again stretches like any drawing.
    const groups = s.groups.map((g) => {
      if (!g.shapeIds.some((id) => bakedIds.has(id)) || g.repeat?.kind === 'index' || !(g.resize.u === 'fixed' && g.resize.n === 'fixed')) return g;
      const stillFormula = shapes.some((x) => g.shapeIds.includes(x.id) && x.expr);
      return stillFormula ? g : { ...g, resize: { u: 'stretch' as const, n: 'stretch' as const } };
    });
    s = { ...s, shapes, groups };
  }


  if (s.handles) {
    const handles = s.handles;
    const fraction = (p: NonNullable<typeof handles.start>): NonNullable<typeof handles.start> =>
      isFormulaHandlePoint(p) ? [roundCoord(expressionValue(p.u, vars) / s.defaultSize.width), roundCoord(expressionValue(p.n, vars) / s.defaultSize.depth)] : p;
    const depth = (handles.depth ?? []).filter((_, i) => !remove.has(`handle:${i}`));
    s = {
      ...s,
      handles: {
        ...(handles.start ? { start: remove.has('ends') ? fraction(handles.start) : handles.start } : {}),
        ...(handles.end ? { end: remove.has('ends') ? fraction(handles.end) : handles.end } : {}),
        ...(depth.length > 0 ? { depth } : {}),
      },
    };
  }

  if (s.stepSyncs) {
    const kept = s.stepSyncs.filter((_, i) => !remove.has(`sync:${i}`));
    const { stepSyncs: _old, ...rest } = s;
    s = kept.length > 0 ? { ...rest, stepSyncs: kept } : rest;
  }
  if (remove.has('depth')) {
    const { depthLink: _old, ...rest } = s;
    s = rest;
  }

  // Values last: wherever a removed value is still used, its number takes its place.
  const removedValues = (spec.formulas ?? []).filter((f) => remove.has(`value:${f.key}`));
  for (const f of removedValues) {
    const value = String(Number((vars[f.key] ?? 0).toFixed(6)));
    const pattern = new RegExp(`(?<![A-Za-z0-9_.])${f.key}(?![A-Za-z0-9_]|\\s*\\()`, 'g');
    s = mapExpressions(s, (src) => src.replace(pattern, value.startsWith('-') ? `(${value})` : value));
  }
  if (removedValues.length > 0) {
    const kept = (s.formulas ?? []).filter((f) => !remove.has(`value:${f.key}`));
    const { formulas: _old, ...rest } = s;
    s = kept.length > 0 ? { ...rest, formulas: kept } : rest;
  }
  return s;
}
